import React from 'react'
import { IntlShape } from 'react-intl'

import { format, parseISO } from 'date-fns'

import {
  CLOSE_SESSION_BUTTON_TEXT,
  CLOSE_SESSION_DURING_VIDEO_CALL_TOOLTIP,
} from '@lyrahealth-inc/shared-app-logic/src/features/calendar/provider/messages'
import { CLOSE_SESSION_TYPES } from '@lyrahealth-inc/shared-app-logic/src/features/calendar/provider/types'
import { CheckInIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/CheckInIcon'
import { NotesIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/NotesIcon'
import { TaskListItemProps } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/taskListItem/TaskListItem'
import { BodyTextSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import { ProviderTaskItem } from './types'
import { DaysOldLabel, getDaysOld } from '../../../../../dashboard/openItems/components/DaysOldLabel'
import { getSessionTypeLabel } from '../../../../../dashboard/openItems/components/getSessionTypeLabel'
import { useNavigateToCloseSession } from '../../../../../dashboard/openItems/hooks/useNavigateToCloseSession'
import { useNoteNavigationActions } from '../../../../clientNotes/hooks/useNoteNavigationActions'

type NavigateToCloseSession = ReturnType<typeof useNavigateToCloseSession>
type NoteNavigationActions = ReturnType<typeof useNoteNavigationActions>

export const mapProviderTaskToListItem = ({
  task,
  clientId,
  intl,
  index,
  isDAEngageProvider,
  videoSessionAppointmentId,
  navigateToCloseSession: { closeSession, completeNote },
  noteNavigationActions: { addSessionNote, navigateToExistingNote },
}: {
  task: ProviderTaskItem
  clientId: string
  intl: IntlShape
  index: number
  isDAEngageProvider: boolean
  videoSessionAppointmentId: number | null
  navigateToCloseSession: NavigateToCloseSession
  noteNavigationActions: NoteNavigationActions
}): TaskListItemProps => {
  const { appointment, episodeId } = task
  const sessionLabel = getSessionTypeLabel(intl, appointment)
  const formattedDate = format(parseISO(appointment.startDate), 'MMM d, yyyy')

  const { icon, taskType } =
    task.kind === 'closeSession'
      ? {
          icon: <CheckInIcon size={24} />,
          taskType: intl.formatMessage({ defaultMessage: 'Close session', description: 'Task type label' }),
        }
      : task.kind === 'daEngageTask'
        ? {
            icon: <NotesIcon size={24} />,
            taskType: intl.formatMessage(CLOSE_SESSION_BUTTON_TEXT[task.closeSessionType]),
          }
        : {
            icon: <NotesIcon size={24} />,
            taskType: intl.formatMessage({ defaultMessage: 'Complete note', description: 'Task type label' }),
          }

  const closesSession =
    task.kind === 'closeSession' ||
    (task.kind === 'daEngageTask' && task.closeSessionType !== CLOSE_SESSION_TYPES.NOTE_NEEDED)
  const isInVideoCall = closesSession && appointment.appointmentId === videoSessionAppointmentId

  // Mirrors how Care Timeline routes these buttons. `completeNote` is the DA-Engage invoicing flow;
  // `NOTE_NEEDED` is the only daEngageTask outcome that routes there, the rest close the session.
  // Everyone else resumes an existing note or starts a new one.
  const onPress = () => {
    if (closesSession) {
      return closeSession(appointment, clientId)
    }
    if (task.kind === 'daEngageTask' || isDAEngageProvider) {
      return completeNote(appointment, clientId)
    }
    return task.existingNoteId
      ? navigateToExistingNote({ noteId: task.existingNoteId })
      : addSessionNote({ appointmentId: appointment.appointmentId, episodeId })
  }

  return {
    icon,
    taskType,
    title: `${formattedDate} · ${sessionLabel}`,
    rightText: (
      <DaysOldLabel
        daysOld={getDaysOld(appointment.startDate)}
        variant={isDAEngageProvider ? 'daysOld' : 'dueDaysAgo'}
        size={BodyTextSize.SMALL}
      />
    ),
    taskTypeSize: BodyTextSize.SMALL,
    testId: tID(`TasksModule-row-${index}`),
    onPress,
    disabled: isInVideoCall,
    disabledTooltipContent: isInVideoCall ? intl.formatMessage(CLOSE_SESSION_DURING_VIDEO_CALL_TOOLTIP) : undefined,
  }
}
